import React, { useState, useEffect } from 'react';
import { useRouterParam } from '../../../../model/history';
import { Container } from '../../../../components/Container/Container';
import { getPageTitle, useMeta } from '../../../../model/meta';
import { ordersPath, orderPath } from '../../../../routes';
import { useRemoteData, useTask } from '../../../../model/data';
import { getOrder } from '../../../../model/order';
import { Card } from '../../../../components/Card/Card';
import { LoadingPage } from '../../../../components/LoadingPage/LoadingPage';
import { StatusPill } from '../../../../components/StatusPill/StatusPill';
import { ControlledModal } from '../../../../components/Modal/ControlledModal';
import { Address, getCompanyDetails } from '../../../../model/company';
import { EditInvoice } from './EditInvoice';
import { PermissionButton } from '../../../../components/Button/PermissionButton';
import { Button } from '../../../../components/Button/Button';
import { updateInvoice, pollInvoiceUntilUrl, unbookInvoice } from '../../../../model/invoice';
import { handleError } from '../../../../model/notification';
import { t } from '../../../../locales/translate';
import { useReloadKey, generateMailToLink, useErrorHandler } from '../../../../util/util';
import { SimpleCheckbox } from '../../../../components/Form/Checkbox/Checkbox';
import { runClearingInvoiceDebugExport } from '../../../../model/exports';
import { useConfirmation } from '../../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { Stack } from '../../../../components/Stack/Stack';

async function performUnbookInvoice(orderId: string, invoiceId?: string): Promise<void> {
  if (!invoiceId) return;

  try {
    await unbookInvoice(invoiceId);
    await getOrder(orderId);
  } catch (error) {
    handleError(error);
  }
}

export function Invoice(): JSX.Element {
  const [editModal, setEditModal] = useState(false);
  const { orderId, invoiceId } = useRouterParam<{
    orderId: string;
    invoiceId: string;
  }>();
  const [key, reload] = useReloadKey();
  const [invoice, loading, error] = useRemoteData(
    () => getOrder(orderId),
    (db) => {
      const order = db.table('orders').find(orderId);
      if (order) {
        const orderInvoice = order.invoices.find((item) => item.id === invoiceId);
        if (orderInvoice) {
          return {
            order,
            ...orderInvoice,
          };
        }
      }
      return undefined;
    }
  );

  const [loadingUpdateStatus, setLoadingUpdateStatus] = useState(false);
  const [loadingIframe, setLoadingIframe] = useState(true);

  const [isPollingUrl, setIsPollingUrl] = useState(false);
  useEffect(() => {
    if (!loading && !isPollingUrl && invoice && !invoice.documentUrl) {
      setIsPollingUrl(true);
      pollInvoiceUntilUrl(invoice, setIsPollingUrl);
    }
  }, [loading, invoice, invoice?.documentUrl]);

  const downloadDebugTask = useTask({ handleError: true });
  useMeta({
    title: getPageTitle(loading && !invoice, invoice?.order?.reference ?? 'not found', invoice),
    breadcrumbs: [
      { title: t.model.order._plural, url: ordersPath() },
      {
        title: getPageTitle(loading && !invoice, invoice?.order?.reference ?? 'not found', invoice),
        url: orderPath(invoice ? invoice.order.id : ''),
      },
      {
        title: getPageTitle(loading && !invoice, invoice ? invoice.reference : 'not found', invoice),
      },
    ],
  });

  const [skipEmailDelivery, setSkipEmailDelivery] = useState(false);

  const { confirm: confirmUnbookInvoice } = useConfirmation({
    title: t.model.invoice.unbookInvoice,
    children: (
      <Stack>
        <p>{t.model.invoice.unbookInvoiceMessage}</p>
        <p>{t.model.invoice.unbookInvoiceWarning}</p>
      </Stack>
    ),
    action: () => performUnbookInvoice(orderId, invoice?.id),
  });

  async function updateInvoiceStatus(status: 'pending' | 'booked'): Promise<void> {
    if (!invoice) {
      return;
    }

    setLoadingUpdateStatus(true);
    try {
      await updateInvoice({
        orderId: invoice.order.id,
        invoiceId: invoice.id,
        status,
        skipEmailDelivery,
      });
    } catch (error) {
      handleError(error);
    }
    setLoadingUpdateStatus(false);
  }

  function activateButton(): JSX.Element | null {
    if (!invoice) {
      return null;
    }

    switch (invoice.status) {
      case 'draft':
        return (
          <Button
            type="secondary"
            onClick={() => updateInvoiceStatus('pending')}
            title={t.actions.place(t.model.invoice._name)}
            loading={loadingUpdateStatus}
          />
        );
      case 'pending':
        return (
          <Button
            type="secondary"
            onClick={() => updateInvoiceStatus('booked')}
            title={t.actions.book(t.model.invoice._name)}
            loading={loadingUpdateStatus}
          />
        );
      default:
        return null;
    }
  }

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.invoice._name)} />;
  } else if (error || !invoice) {
    return <Container>{t.errors.itemNotFound(t.model.invoice._name)}.</Container>;
  }

  return (
    <Container className="invoice-details">
      <Card
        title={
          <>
            {`${t.model.invoice._name} - ${invoice.reference} `}
            <StatusPill status={invoice.status} />
          </>
        }
        actions={
          <>
            {invoice.status !== 'booked' && (
              <SimpleCheckbox
                value={skipEmailDelivery}
                onChange={() => setSkipEmailDelivery((value) => !value)}
                label={t.model.payment.skipCustomerEmail}
              />
            )}
            {activateButton()}
            {invoice.$permissions.includes('unbook') && (
              <Button type="secondary" title={t.model.invoice.unbookInvoice} onClick={() => confirmUnbookInvoice()} />
            )}
            {(invoice.order.type === 'merch_clearing' || invoice.order.type === 'event_clearing') &&
              invoice.documentUrl && (
                <SendInvoiceAsMailButton
                  companyId={invoice.order.company.id}
                  documentUrl={invoice.documentUrl}
                  formattedBookingNumber={invoice.formattedBookingNumber}
                />
              )}
            <PermissionButton model={invoice} permission="update" name="edit" onClick={() => setEditModal(true)} />
          </>
        }
      >
        {(loadingIframe || isPollingUrl) && (
          <span style={{ display: 'flex', alignContent: 'center' }}>
            <LoadingPage title={t.actions.loading(t.model.invoice._name)} />
          </span>
        )}
        {!isPollingUrl && (
          <iframe
            key={key}
            src={invoice.documentUrl}
            width="100%"
            onLoad={() => {
              setLoadingIframe(false);
            }}
            frameBorder="0"
            style={{ height: 'calc(100vh - 200px)' }}
          />
        )}
        {invoice.order.type?.includes('clearing') && (
          <Button
            title={t.model.invoice.downloadDetails}
            icon="download"
            loading={downloadDebugTask.loading}
            onClick={() => downloadDebugTask.run(runClearingInvoiceDebugExport(invoice.id))}
          />
        )}
      </Card>
      <ControlledModal<Address>
        open={editModal}
        model={invoice.address}
        onClose={() => setEditModal(false)}
        title={t.actions.edit(t.model.invoice._name)}
        size="small"
      >
        {(controller) => (
          <EditInvoice
            onSubmit={() => {
              setLoadingIframe(true);
              reload();
            }}
            invoice={invoice}
            controller={controller}
          />
        )}
      </ControlledModal>
    </Container>
  );
}

const SendInvoiceAsMailButton = ({
  companyId,
  documentUrl,
  formattedBookingNumber,
}: {
  companyId: string;
  documentUrl: string;
  formattedBookingNumber: string;
}): JSX.Element => {
  const [company, loading, error] = useRemoteData(
    () => getCompanyDetails(companyId),
    (db) => db.table('companies').find(companyId)
  );

  useErrorHandler(error);

  return (
    <a
      href={generateMailToLink({
        email: company?.billingEmails || [],
        subject: t.model.invoice.clearingEmailSubject(formattedBookingNumber),
        body: t.model.invoice.clearingEmailBody(documentUrl),
      })}
    >
      <Button type="secondary" loading={loading} disabled={loading} title={t.actions.send(t.model.user.email)} />
    </a>
  );
};
